// P02 Price Waterfall, P08 Trade Promo ROI, P12 B2B Quotation, P13 Working Capital.
import { useMemo, useState } from "react";
import { priceWaterfall, tradePromoRoi, quotation, workingCapital, inr, pct } from "./engines";
import { Field, ResultRow, Note, ToolSection, useNumbers } from "./ui";

export function ToolP02() {
  const [list, setList] = useState(1000);
  const [steps, setSteps] = useState([
    { label: "Invoice discount", pct: 8, base: "running" },
    { label: "Cash discount", pct: 2, base: "running" },
    { label: "Retrospective rebate", pct: 4, base: "list" },
    { label: "Dealer scheme", pct: 3, base: "running" },
  ]);
  const r = useMemo(() => priceWaterfall({ listPrice: list, steps: steps.filter((s) => s.label) }), [list, steps]);
  const setS = (i, k) => (val) => setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, [k]: val } : s)));

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="List / contract price per unit" prefix="₹" value={list} onChange={setList} testid="p02-list" />
        <div className="overline">Deductions, in the order they apply</div>
        {steps.map((s, i) => (
          <div key={i} className="border border-zinc-200 bg-white p-3 grid grid-cols-[1fr_70px_110px] gap-2 items-center">
            <input className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" value={s.label} onChange={(e) => setS(i, "label")(e.target.value)} data-testid={`p02-label-${i}`} />
            <input type="number" className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" value={s.pct} onChange={(e) => setS(i, "pct")(e.target.value)} data-testid={`p02-pct-${i}`} />
            <select className="text-xs px-2 py-1.5 border border-zinc-200" value={s.base} onChange={(e) => setS(i, "base")(e.target.value)} data-testid={`p02-base-${i}`}>
              <option value="running">% of running</option>
              <option value="list">% of list</option>
            </select>
          </div>
        ))}
        <button type="button" onClick={() => setSteps((ss) => [...ss, { label: "", pct: 0, base: "running" }])} className="text-sm font-medium underline underline-offset-4" data-testid="p02-add">+ Add deduction</button>
        <Note>Bases matter: a 2% cash discount applies to the invoice value after earlier discounts, while a retrospective rebate usually applies to list. The tool keeps them distinct — the classic waterfall error is applying everything to list.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Gross-to-net</div>
        <div className="mt-3">
          <ResultRow label="List price" value={inr(r.list)} />
          {r.rows.map((row, i) => (
            <ResultRow key={i} label={`${row.label} (${row.pct}% of ${row.base})`} value={`−${inr(row.deduction)} → ${inr(row.after)}`} />
          ))}
          <ResultRow strong label="Pocket price" value={inr(r.pocket)} />
          <ResultRow strong label="Realisation" value={pct(r.realizationPct / 100)} tone={r.realizationPct < 80 ? "bad" : "good"} />
        </div>
        <div className="mt-4"><Note>Realisation under ~85% usually means exceptions have become the norm. Set an exception threshold: any deal below a chosen pocket price needs commercial-head sign-off, not salesperson discretion.</Note></div>
      </div>
    </div>
  );
}

export function ToolP08() {
  const [v, set] = useNumbers({ baselineUnits: 1000, promotedUnits: 1600, regularPrice: 100, promoPrice: 90, variableCost: 60, schemeCost: 8000, cannibalPct: 15, pullForwardPct: 10 });
  const r = useMemo(() => tradePromoRoi(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Baseline units (normal period)" value={v.baselineUnits} onChange={set("baselineUnits")} testid="p08-base" />
          <Field label="Units sold in promo" value={v.promotedUnits} onChange={set("promotedUnits")} testid="p08-promo" />
          <Field label="Regular price" prefix="₹" value={v.regularPrice} onChange={set("regularPrice")} testid="p08-price" />
          <Field label="Promo price" prefix="₹" value={v.promoPrice} onChange={set("promoPrice")} testid="p08-promoprice" />
          <Field label="Variable cost / unit" prefix="₹" value={v.variableCost} onChange={set("variableCost")} testid="p08-cost" />
          <Field label="Total scheme cost" prefix="₹" value={v.schemeCost} onChange={set("schemeCost")} testid="p08-scheme" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Cannibalisation of full-price sales" suffix="%" value={v.cannibalPct} onChange={set("cannibalPct")} testid="p08-cannibal" />
          <Field label="Forward buying (pull-forward)" suffix="%" value={v.pullForwardPct} onChange={set("pullForwardPct")} testid="p08-pull" />
        </div>
        <Note>Observed uplift is not incremental uplift. Cannibalisation and pull-forward are your declared assumptions — the honest post-mortem states them rather than hiding them.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Promotion post-mortem</div>
        <div className="mt-3">
          <ResultRow label="Observed uplift" value={`${r.observedUpliftUnits} units`} />
          <ResultRow label="Less: cannibalised + pulled forward" value={`−${Math.round(r.cannibalizedUnits + r.pullForwardUnits)} units`} />
          <ResultRow strong label="Truly incremental" value={`${r.incrementalUnits} units → ${inr(r.incrementalContribution)}`} />
          <ResultRow label="Scheme cost" value={inr(r.schemeCost)} />
          <ResultRow strong label="Promotion ROI" value={r.roiPct === null ? "—" : pct(r.roiPct / 100)} tone={r.roiPct !== null && r.roiPct < 0 ? "bad" : "good"} />
          <ResultRow label="Break-even incremental uplift" value={r.breakEvenUpliftUnits ? `${r.breakEvenUpliftUnits} units` : "—"} />
        </div>
        {r.roiPct !== null && r.roiPct < 0 && <div className="mt-4"><Note tone="warn">This promotion destroyed contribution. Before the next scheme, reset the uplift bar or renegotiate the funding.</Note></div>}
      </div>
    </div>
  );
}

export function ToolP12() {
  const [v, set] = useNumbers({ quantity: 100, pricePerUnit: 500, materialPerUnit: 200, laborPerUnit: 80, overheadPct: 15, tooling: 2000, packing: 500, shipping: 1000, creditDays: 60, financeRatePct: 12 });
  const r = useMemo(() => quotation(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Quantity" value={v.quantity} onChange={set("quantity")} testid="p12-qty" />
          <Field label="Quoted price / unit" prefix="₹" value={v.pricePerUnit} onChange={set("pricePerUnit")} testid="p12-price" />
          <Field label="Material / unit" prefix="₹" value={v.materialPerUnit} onChange={set("materialPerUnit")} testid="p12-material" />
          <Field label="Labour / unit" prefix="₹" value={v.laborPerUnit} onChange={set("laborPerUnit")} testid="p12-labour" />
          <Field label="Overhead allocation" suffix="%" value={v.overheadPct} onChange={set("overheadPct")} testid="p12-overhead" />
          <Field label="Tooling / customisation (one-off)" prefix="₹" value={v.tooling} onChange={set("tooling")} testid="p12-tooling" />
          <Field label="Packing (total)" prefix="₹" value={v.packing} onChange={set("packing")} testid="p12-packing" />
          <Field label="Shipping (total)" prefix="₹" value={v.shipping} onChange={set("shipping")} testid="p12-shipping" />
          <Field label="Credit period (days)" value={v.creditDays} onChange={set("creditDays")} testid="p12-credit" />
          <Field label="Finance rate" suffix="% p.a." value={v.financeRatePct} onChange={set("financeRatePct")} testid="p12-rate" />
        </div>
        <Note>Ninety-day credit on thin-margin orders is a hidden price cut — the finance cost line makes it visible before you sign.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Quote economics</div>
        <div className="mt-3">
          <ResultRow label="Order revenue" value={inr(r.revenue)} />
          <ResultRow label="Direct + overhead + other" value={inr(r.directCost + r.overhead + r.otherCosts)} />
          <ResultRow label="Credit financing cost" value={inr(r.financeCost)} />
          <ResultRow strong label="Order contribution" value={`${inr(r.contribution)} (${r.contributionPct}%)`} tone={r.profitable ? "good" : "bad"} />
          <ResultRow strong label="Floor price / unit" value={inr(r.floorPricePerUnit)} />
        </div>
        {!r.profitable && <div className="mt-4"><Note tone="warn">This quote loses money as entered. Negotiate quantity, terms or price — or walk away deliberately.</Note></div>}
      </div>
    </div>
  );
}

export function ToolP13() {
  const [v, set] = useNumbers({ annualSales: 36500000, annualCogs: 21900000, receivables: 5000000, inventory: 3000000, payables: 2000000 });
  const r = useMemo(() => workingCapital(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Annual net sales" prefix="₹" value={v.annualSales} onChange={set("annualSales")} testid="p13-sales" />
        <Field label="Annual COGS" prefix="₹" value={v.annualCogs} onChange={set("annualCogs")} testid="p13-cogs" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="Receivables" prefix="₹" value={v.receivables} onChange={set("receivables")} testid="p13-ar" />
          <Field label="Inventory" prefix="₹" value={v.inventory} onChange={set("inventory")} testid="p13-inv" />
          <Field label="Payables" prefix="₹" value={v.payables} onChange={set("payables")} testid="p13-ap" />
        </div>
        <Note>DSO is measured on sales; DIO and DPO on COGS — mixing the bases is the classic CCC error. Use average balances, not year-end snapshots.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Cash conversion</div>
        <div className="mt-3">
          <ResultRow label="DSO (receivable days)" value={`${r.dso} days`} tone={r.dso > 60 ? "bad" : undefined} />
          <ResultRow label="DIO (inventory days)" value={`${r.dio} days`} tone={r.dio > 75 ? "bad" : undefined} />
          <ResultRow label="DPO (payable days)" value={`${r.dpo} days`} />
          <ResultRow strong label="Cash conversion cycle" value={`${r.ccc} days`} tone={r.ccc > 60 ? "bad" : "good"} />
          <ResultRow label="Cash released per DSO day improved" value={inr(r.releasePerDayDso)} />
          <ResultRow label="5-day DSO improvement releases" value={inr(r.release5Days)} tone="good" />
        </div>
      </div>
    </div>
  );
}
